<template>
    <div id="roles">
        <supsis-table
            :columns="cols"
            :rows="currentPageData"
            :bottom-pagination="true"
            :pagination-cursor="paginationCursor"
            @cursor-changed="(cursor) => (paginationCursor = cursor)"
        >
            <template #header>
                <div class="d-flex justify-content-between align-items-center">
                    <a-input-search
                        v-model="searchText"
                        :placeholder="$t('message.startFilter')"
                        class="search-input"
                        @search="onSearch"
                    />
                    <SupsisButton ghost @click="openCreateFormModal"><md-icon>add</md-icon> Create Role</SupsisButton>
                </div>
            </template>
        </supsis-table>
        <Modal2
            v-if="isModalShow"
            id="role-form"
            :title="editMode ? $t('message.updateRoleForm') : $t('message.newRoleForm')"
            style="overflow-x: auto; overflow-y: hidden"
            @cancel="hideModal"
            :show-footer="false"
        >
            <a-alert
                v-if="$validator.errors.items.length"
                :message="$t('message.error')"
                class="mb-2"
                showIcon
                type="error"
            >
                <div slot="description">
                    <div v-for="err of $validator.errors.items" :key="err.id">
                        <span>
                            {{ err.msg }}
                        </span>
                    </div>
                </div>
            </a-alert>
            <b-form class="d-flex flex-column justify-content-center" @submit.prevent="duplicateControl">
                <b-row class="mb-3">
                    <b-col md="12">
                        <b-form-input
                            id="display-name-input"
                            v-model="role.name"
                            v-validate="'required'"
                            :name="$t('message.roleName')"
                            :placeholder="$t('message.roleName')"
                            autocomplete="off"
                            trim
                            type="text"
                        />
                        <a-checkbox
                            :checked="isEveryIncludes(permissions.map((x) => x._id))"
                            :indeterminate="isSomeIncludes(permissions.map((x) => x._id))"
                            style=""
                            @click="selectAll"
                        >
                            <label class="col-form-label" @click="selectAll">{{
                                $t("message.selectAllPermission")
                            }}</label>
                        </a-checkbox>
                    </b-col>
                    <b-col md="12">
                        <b-row>
                            <b-col v-for="(permissions, index) in groupedPermissions" :key="index" md="12">
                                <div>
                                    <div class="groupTitle">
                                        <a-checkbox
                                            :checked="isEveryIncludes(permissions.map((x) => x._id))"
                                            :indeterminate="isSomeIncludes(permissions.map((x) => x._id))"
                                            @click="
                                                groupClicked(
                                                    index,
                                                    permissions.map((x) => x._id)
                                                )
                                            "
                                        >
                                            <label
                                                @click="
                                                    groupClicked(
                                                        index,
                                                        permissions.map((x) => x._id)
                                                    )
                                                "
                                            >
                                                <span
                                                    v-if="
                                                        permissions.length > 0 &&
                                                        PERMISSION_TYPES[permissions[0].name] !== undefined
                                                    "
                                                >
                                                    {{
                                                        PERMISSION_TYPES[permissions[0].name][$i18n.locale].entity
                                                    }}</span
                                                >
                                                <span v-else>{{ index }}</span>
                                            </label>
                                        </a-checkbox>
                                        <md-button
                                            id="mdButton"
                                            style="
                                                width: 0px;
                                                min-width: 32px;
                                                margin: 0;
                                                height: 100%;
                                                background-color: #f3f6f9;
                                                flex: 1;
                                            "
                                            @click="toggles[index] = !toggles[index]"
                                        >
                                            <md-icon style="margin: 0">
                                                keyboard_arrow_{{ toggles[index] ? "up" : "down" }}
                                            </md-icon>
                                        </md-button>
                                    </div>
                                    <b-form-checkbox-group
                                        v-if="toggles[index]"
                                        v-model="role.permissions"
                                        :options="
                                            permissions.map((p) => ({
                                                text: PERMISSION_TYPES[p.name]
                                                    ? PERMISSION_TYPES[p.name][$i18n.locale].description
                                                    : p.description,
                                                value: p._id,
                                            }))
                                        "
                                        class="mx-3 mb-3"
                                        name="permissions"
                                        stacked
                                    ></b-form-checkbox-group>
                                </div>
                            </b-col>
                        </b-row>
                    </b-col>
                </b-row>

                <b-row>
                    <b-col class="d-flex justify-content-end modal-buttons" md="12">
                        <b-button class="custom-modal-button mr-3" @click="this.hideModal">
                            <template>
                                {{ $t("message.cancel") }}
                            </template>
                        </b-button>
                        <b-button :disabled="loading" class="custom-modal-button" type="submit" variant="primary">
                            <template v-if="!loading">
                                <span v-if="editMode">
                                    {{ $t("message.update") }}
                                </span>
                                <span v-else>
                                    {{ $t("message.save") }}
                                </span>
                            </template>

                            <template v-else>
                                <font-awesome-icon icon="spinner" spin />
                            </template>
                        </b-button>
                    </b-col>
                </b-row>
            </b-form>
        </Modal2>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faPencilAlt, faPlusCircle, faSpinner, faTimes } from "@fortawesome/free-solid-svg-icons";
import { log2 } from "../../../../utils/log2";
import * as _ from "lodash";
import { PERMISSION_TYPES } from "../../../../language/permissionTypes";
import { sendSocketMessage } from "../../../../utils/socketStream";
import Pagination from "../../../../components/Pagination.vue";
import ActionEdit from "../../../../components/svg/ActionEdit.vue";
import ActionDelete from "../../../../components/svg/ActionDelete.vue";
import Modal2 from "../../../../components/common/Modal2.vue";
import SupsisTable from "../../../../components/SupsisTable.vue";
import SupsisButton from "../../Settings/components/SupsisButton.vue";
import CreateButton from "../components/CreateButton.vue";

library.add(faPencilAlt, faTimes, faPlusCircle, faSpinner);
const log = log2("Roles");
const roleInitialObject = {
    _id: null,
    name: "",
    isRoot: true,
    permissions: [],
};
export default {
    name: "Roles",
    components: {
        CreateButton,
        SupsisButton,
        SupsisTable,
        Pagination,
        ActionEdit,
        ActionDelete,
        Modal2,
    },
    data() {
        return {
            PERMISSION_TYPES,
            isBusy: true,
            loading: false,
            editMode: false,
            searchText: undefined,
            role: {
                ...roleInitialObject,
            },
            cols: [
                {
                    field: "name",
                    key: "name",
                    title: this.$t("message.title"),
                },
                {
                    align: "right",
                    field: "actionsSlot",
                    key: "actionsSlot",
                    title: this.$t("message.transactions"),
                    fixed: "right",
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        const renderEditButton = () => {
                            return (
                                this.checkPermission(PERMISSION_TYPES["role.update"].tr.name) && (
                                    <a-button
                                        class="actionButton d-flex align-items-center"
                                        on-click={() => this.edit(row._id)}
                                    >
                                        <ActionEdit />
                                    </a-button>
                                )
                            );
                        };
                        const renderDeleteButton = () => {
                            return (
                                this.checkPermission(PERMISSION_TYPES["role.delete"].tr.name) && (
                                    <a-button
                                        class="actionButton d-flex align-items-center"
                                        on-click={() => this.remove(row._id)}
                                    >
                                        <ActionDelete />
                                    </a-button>
                                )
                            );
                        };
                        return (
                            <div class="d-flex w-100 justify-content-end">
                                {renderEditButton()}
                                {renderDeleteButton()}
                            </div>
                        );
                    },
                },
            ],
            currentSort: {
                columnKey: "name",
                order: "ascend",
            },
            toggles: {},
            isModalShow: this.$route.query.action == "create",
            editID: null,
            paginationCursor: {
                total: 0,
                page: 1,
                perPage: 10,
            },
        };
    },
    computed: {
        ...mapState({
            roles: (state) => state.role.roles.data,
            site: (state) => state.site.site,
            permissions: (state) => state.permission.permissions,
            isAdmin: (state) => state.auth.adminUser != null,
        }),
        ...mapGetters({
            groupedPermissions: "permission/groupedPermissions",
            isDarkMode: "user/isDarkMode",
            checkPermission: "auth/checkPermission",
        }),
        total() {
            return this.getFilteredRows().length;
        },
        currentPageData() {
            const start = (this.paginationCursor.page - 1) * this.paginationCursor.perPage;
            const end = start + this.paginationCursor.perPage;
            return this.getFilteredRows().slice(start, end);
        },
    },
    methods: {
        ...mapActions({
            getPermissions: "permission/getPermissions",
            getRoles: "role/getRoles",
            getRole: "role/getRole",
            createRole: "role/createRole",
            updateRole: "role/updateRole",
            deleteRole: "role/deleteRole",
        }),
        showModal() {
            this.isModalShow = true;
        },
        hideModal() {
            this.isModalShow = false;
            this.reset();
        },
        getFilteredRows() {
            return this.searchText
                ? this.roles.filter((x) =>
                      x.name.toLocaleLowerCase("tr-TR").includes(this.searchText.toLocaleLowerCase("tr-TR"))
                  )
                : this.roles;
        },
        onSearch(value) {
            log("onSearch", value);
            this.searchText = value;
        },
        groupClicked(key, permissions) {
            const someIncludes = _.some(this.role.permissions, (q) => permissions.includes(q));
            if (someIncludes) {
                this.role.permissions = this.role.permissions.filter((x) => !permissions.includes(x));
            } else {
                this.role.permissions = _.uniq([...this.role.permissions, ...permissions]);
            }

            log.debug("groupClicked", {
                key,
                permissions,
                current: this.role.permissions,
            });
        },
        selectAll() {
            let allPermissions = Object.values(this.groupedPermissions)
                .reduce((acc, curr) => [...acc, ...curr], [])
                .map((x) => x._id);
            if (allPermissions.length === this.role.permissions.length) this.role.permissions = [];
            else this.role.permissions = allPermissions;
        },
        isEveryIncludes(permissionIds) {
            return _.every(permissionIds, (q) => this.role.permissions.includes(q));
        },
        isSomeIncludes(permissionIds) {
            return this.isEveryIncludes(permissionIds)
                ? false
                : _.some(permissionIds, (q) => this.role.permissions.includes(q));
        },
        get(getPermissions = false) {
            this.isBusy = true;
            this.getRoles({ perPage: this.paginationCursor.perPage, page: this.paginationCursor.page })
                .then((roles) => {
                    this.paginationCursor.total = roles.total;
                    this.isBusy = false;

                    if (getPermissions) {
                        this.getPermissions({ perPage: 100, page: 1 });
                    }
                    log("groupedPermissions", this.groupedPermissions);
                    this.toggles = Object.keys(this.groupedPermissions).reduce((acc, x) => {
                        acc[x] = false;
                        return acc;
                    }, {});
                    this.role.permissions = Object.values(this.groupedPermissions)
                        .reduce((acc, curr) => [...acc, ...curr], [])
                        .map((x) => x._id);
                })
                .catch((err) => {
                    log.error(err);
                    this.isBusy = false;
                });
        },
        openCreateFormModal() {
            this.editMode = false;
            /*this.$bvModal.show("role-form");*/
            this.showModal();
        },
        edit(id) {
            this.editID = id;
            this.getRole(id).then((response) => {
                log("getRole", { id, response });
                this.role = _.cloneDeep(response);

                if (this.role.isRoot) {
                    this.role.permissions = this.permissions.map((p) => p._id);
                } else {
                    this.role.permissions = this.role.permissions.map((p) => p._id);
                }
                this.editMode = true;
                this.showModal();
            });
        },
        remove(id) {
            this.$dialog
                .confirm(this.$t("message.deletePermissionQuestion"), {
                    okText: this.$t("message.continue"),
                    cancelText: this.$t("message.cancel"),
                })
                .then(() => {
                    this.deleteRole(id)
                        .then((x) => {
                            this.$toasted.show(this.$t("message.roleDeleted"), {
                                duration: 3000,
                                position: "bottom-center",
                            });
                        })
                        .catch((err) => {
                            log.error(err.response, err.message);
                            const errMsg =
                                err.response && err.response.data && err.response.data.localizedMessages
                                    ? err.response.data.localizedMessages[this.$i18n.locale]
                                    : err.message;
                            this.$toasted.error(errMsg, {
                                duration: 4000,
                                position: "bottom-center",
                            });
                        });
                });
        },
        reset() {
            this.role = {
                ...roleInitialObject,
            };
            this.get(true);
        },
        duplicateControl() {
            const isThereSameRole = this.roles.find((x) => x.name.toLowerCase() === this.role.name.toLowerCase());
            const duplicateId = isThereSameRole ? isThereSameRole._id : null;
            const isThisSameRole = duplicateId === this.editID;
            if (!isThisSameRole && duplicateId !== null) {
                this.$toasted.error(this.$t("message.sameRole"), {
                    duration: 3000,
                    position: "bottom-center",
                });
                return;
            } else {
                this.handleSave();
            }
        },
        handleSave() {
            this.$validator.validateAll().then((response) => {
                if (response) {
                    this.loading = true;

                    // if was select all permissions set isRoot = true
                    // else set isRoot = false
                    if (
                        this.role.permissions.length ===
                        Object.values(this.groupedPermissions).reduce((acc, curr) => [...acc, ...curr], []).length
                    ) {
                        this.role.isRoot = true;
                        this.role.permissions = [];
                    } else this.role.isRoot = false;

                    const result = this.role._id ? this.updateRole(this.role) : this.createRole(this.role);
                    const isUpdate = this.role._id ? true : false;
                    result
                        .then(() => {
                            this.get(true);
                            /*this.$bvModal.hide("role-form");*/
                            this.hideModal();
                            this.$toasted.show(this.$t("message.roleSaved"), {
                                duration: 3000,
                                position: "bottom-center",
                            });
                            if (isUpdate) {
                                sendSocketMessage("user role-updated", {
                                    siteId: this.site._id,
                                    roleId: this.role._id,
                                });
                            }
                        })
                        .finally(() => (this.loading = false));
                } else {
                    this.loading = false;
                }
            });
        },
        queryCreateCheck() {
            if (this.$route.query.action === "create") {
                this.openCreateFormModal();
            }
        },
    },
    mounted() {
        this.get(true);
        this.queryCreateCheck();
    },
    updated() {
        if (this.$validator.locale !== this.$i18n.locale) this.$validator.localize(this.$i18n.locale);
    },
    watch: {
        paginationCursor: {
            deep: true,
            handler() {
                this.get();
            },
        },
        $route() {
            this.queryCreateCheck();
        },
    },
};
</script>

<style lang="scss">
#roles {
    position: relative;
    height: 100%;

    .groupTitle {
        border-radius: 6px;
        height: 35px;
        display: flex;
        align-items: center;
        margin-bottom: 5px;
        padding: 4px 6px;
        cursor: pointer;
        justify-content: space-between;
        background-color: rgb(243, 246, 249);

        .md-ripple {
            justify-content: flex-end !important;
        }

        label {
            font-family: Poppins;
            //font-size: 12px;
            font-size: 14px;
            font-weight: 600;
            margin: 0;
            padding: 0;
        }

        .ant-checkbox-checked::before {
            content: "";
            position: absolute;
            top: -2px;
            /*  left: -2px;
                right: -2px;*/
            bottom: -2px;
            background: rgba(24, 144, 255, 0.3); /* Hafif parlaklık rengi */
            border-radius: 3px;
        }

        .ant-checkbox-checked:hover {
            .ant-checkbox-inner {
                background-color: rgba(24, 144, 255, 0.3); /* Hafif parlaklık rengi */
                padding: 7px;
            }
        }

        .ant-checkbox-checked .ant-checkbox-inner {
            background-color: #01a9f4 !important;
            border-color: #008ece !important;
        }
    }
}
</style>
